import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { Plus, Trash2, Users, Shield, ShieldCheck, Ban } from 'lucide-react';
import { toast } from 'sonner';
import { api, getUser } from '../../lib/api';
import { formatDate } from '../../lib/utils';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'admin' });
  const currentUser = getUser();

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try { const data = await api.getUsers(); setUsers(data); }
    catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    try {
      await api.register(form);
      toast.success('Benutzer erstellt');
      setDialogOpen(false);
      setForm({ email: '', password: '', name: '', role: 'admin' });
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleToggle = async (id) => {
    try {
      await api.toggleUser(id);
      toast.success('Status geändert');
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Benutzer wirklich löschen?')) return;
    try {
      await api.deleteUser(id);
      toast.success('Benutzer gelöscht');
      loadUsers();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Space Grotesk'}}>Benutzer</h1>
          <p className="text-sm text-muted-foreground mt-1">{users.length} Admin-Benutzer</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="gap-2" data-testid="admin-crud-create-button">
          <Plus className="w-4 h-4" /> Neuer Benutzer
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-600" /></div>
      ) : (
        <div className="space-y-3">
          {users.map(u => (
            <Card key={u.id} className="rounded-xl">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-10 h-10 rounded-xl bg-sky-50 flex items-center justify-center flex-shrink-0">
                  {u.role === 'admin' ? <ShieldCheck className="w-5 h-5 text-sky-600" /> : <Shield className="w-5 h-5 text-slate-400" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-slate-900">{u.name}</span>
                    <Badge variant={u.is_active ? 'default' : 'secondary'} className="text-[10px]">
                      {u.is_active ? 'Aktiv' : 'Gesperrt'}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">{u.role}</Badge>
                    {currentUser?.id === u.id && <Badge className="text-[10px] bg-sky-50 text-sky-700">Sie</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">{u.email} · Erstellt: {formatDate(u.created_at)}</div>
                </div>
                {currentUser?.id !== u.id && (
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleToggle(u.id)} title={u.is_active ? 'Sperren' : 'Aktivieren'}>
                      <Ban className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(u.id)} className="text-red-500">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Neuer Admin-Benutzer</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Name *</Label>
              <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Max Mustermann" />
            </div>
            <div>
              <Label>E-Mail *</Label>
              <Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} placeholder="max@example.de" />
            </div>
            <div>
              <Label>Passwort *</Label>
              <Input type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="Mindestens 6 Zeichen" />
            </div>
            <div>
              <Label>Rolle</Label>
              <Select value={form.role} onValueChange={v => setForm({...form, role: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
            <Button onClick={handleCreate} disabled={!form.email || !form.password || !form.name} data-testid="admin-save-button">Erstellen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
